import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Remove trailing slashes from backend URL to prevent double slashes
const getBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, '');
  const apiUrl = process.env.REACT_APP_API_URL?.replace(/\/+$/, '');
  
  if (backendUrl) {
    return `${backendUrl}/api/v1`;
  }
  if (apiUrl) {
    return apiUrl;
  }
  return 'https://api.st-cael.org/api/v1';
};

const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const PUBLIC_AUTH_PATHS = ['/auth/login', '/auth/forgot-password', '/auth/reset-password'];

function isPublicAuthRequest(url?: string): boolean {
  if (!url) return false;
  return PUBLIC_AUTH_PATHS.some((path) => url.includes(path));
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    if (!isPublicAuthRequest(config.url)) {
      const token = localStorage.getItem('sellerToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      const path = window.location.pathname;
      const onPublicAuthPage =
        path === '/login' ||
        path === '/forgot-password' ||
        path === '/reset-password';

      if (onPublicAuthPage || isPublicAuthRequest(originalRequest.url)) {
        return Promise.reject(error);
      }

      if (window.location.pathname === '/login') {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers && token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        processQueue(null, null);
        isRefreshing = false;
        
        if (window.location.pathname !== '/login') {
          localStorage.removeItem('sellerToken');
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
        return Promise.reject(new Error('Session expired. Please log in again.'));
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        isRefreshing = false;
        
        if (window.location.pathname !== '/login') {
          localStorage.removeItem('sellerToken');
          setTimeout(() => {
            window.location.href = '/login';
          }, 100);
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

